<template>
  <div class="flex flex-col w-[280px] sm:w-[300px] flex-shrink-0 rounded-lg h-full">
    <!-- Column Header -->
    <div
      class="px-3 sm:px-4 py-3 border-b border-opacity-20 rounded-t-lg"
      :style="{
        backgroundColor: stage.color || '#94A3B8',
        borderColor: stage.color || '#94A3B8'
      }"
    >
      <h3 class="text-xs sm:text-sm font-bold text-white m-0 mb-2 truncate">{{ stage.name }}</h3>
      <div class="flex items-center justify-between text-xs">
        <span class="px-2 py-1 bg-white bg-opacity-20 text-white rounded font-medium">
          {{ localDeals.length }}
        </span>
        <span class="font-bold text-white text-xs sm:text-sm">
          {{ formatCurrency(totalValue) }}
        </span>
      </div>
    </div>

    <!-- Column Content -->
    <div
      class="flex-1 overflow-y-auto p-2 transition-colors rounded-b-lg"
      :class="{ 'ring-2 ring-inset ring-white/40': isDragOver }"
      :style="{ backgroundColor: hexToRgba(stage.color || '#94A3B8', isDragOver ? 0.18 : 0.1) }"
      @dragover.prevent="isDragOver = true"
      @dragleave="isDragOver = false"
      @drop="isDragOver = false"
    >
      <Draggable
        :list="localDeals"
        group="pipeline-deals"
        :animation="180"
        force-fallback
        ghost-class="deal-ghost"
        chosen-class="deal-chosen"
        fallback-class="deal-fallback"
        item-key="id"
        class="flex flex-col gap-2 min-h-[40px]"
        @change="onDraggableChange"
      >
        <template #item="{ element }">
          <deal-card
            :deal="element"
            @click="$emit('dealClick', element)"
            @edit="$emit('dealClick', element)"
            @open-conversation="$emit('openConversation', $event)"
            @open-contact="$emit('openContact', $event)"
          />
        </template>

        <template #footer>
          <div v-if="localDeals.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
            <p class="text-xs sm:text-sm text-n-slate-10">{{ $t('PIPELINES.COLUMN.EMPTY') }}</p>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import DealCard from './DealCard.vue';

export default {
  components: { Draggable, DealCard },
  props: {
    stage: { type: Object, required: true },
    deals: { type: Array, default: () => [] },
  },
  emits: ['dealClick', 'dealMove', 'openConversation', 'openContact'],
  data() {
    return {
      localDeals: [],
      isDragOver: false,
    };
  },
  watch: {
    deals: {
      immediate: true,
      handler(newDeals) {
        this.localDeals = [...newDeals];
      },
    },
  },
  computed: {
    totalValue() {
      return this.localDeals.reduce(
        (sum, deal) => sum + this.getDealDisplayAmount(deal),
        0
      );
    },
  },
  methods: {
    onDraggableChange(evt) {
      if (evt.added) {
        this.$emit('dealMove', {
          dealId: evt.added.element.id,
          targetStageId: this.stage.id,
        });
      }
    },
    getDealDisplayAmount(deal) {
      const amount = Number(deal?.amount);
      if (Number.isFinite(amount) && amount > 0) return amount;

      const calculated = Number(deal?.custom_attributes?.calculated_total);
      if (Number.isFinite(calculated) && calculated > 0) return calculated;

      const fromProducts = (deal?.products || []).reduce((sum, item) => {
        const quantity = Number(item?.quantity || 0);
        const unitPrice = Number(item?.unit_price || 0);
        if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return sum;
        return sum + quantity * unitPrice;
      }, 0);
      return fromProducts > 0 ? fromProducts : 0;
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
      }).format(amount);
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped>
/* Ghost: placeholder em branco onde o card será solto */
:deep(.deal-ghost) {
  opacity: 1 !important;
  background: rgba(148, 163, 184, 0.08) !important;
  border: 2px dashed rgba(148, 163, 184, 0.45) !important;
  border-radius: 12px !important;
  min-height: 80px !important;
}
:deep(.deal-ghost > *) {
  visibility: hidden !important;
}

/* Item selecionado (fase de "segurar") antes de iniciar o drag */
:deep(.deal-chosen) {
  cursor: grabbing !important;
}

/* Quando está ativamente sendo arrastado (drag em curso) */
:deep(.deal-chosen.sortable-drag) {
  opacity: 0 !important;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}
</style>

<!-- Non-scoped: clone do drag é appendado ao body, fora do scope do componente -->
<style>
.deal-fallback {
  opacity: 0.96 !important;
  background-color: var(--color-n-solid-1, #ffffff) !important;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.22), 0 6px 12px rgba(0, 0, 0, 0.12) !important;
  transform: rotate(2deg) scale(1.03) !important;
  cursor: grabbing !important;
  border-radius: 12px !important;
  pointer-events: none !important;
  z-index: 9999 !important;
  transition: none !important;
}
</style>
